/**
 * MSAL.js configuration for Azure AD SSO.
 *
 * Mirrors the founda-docs frontend so the deployment story is identical
 * across the foundaai app fleet. The PublicClientApplication is created
 * once and initialized eagerly so consumers can `await` a stable singleton.
 *
 * When the env vars are unset (local dev without a real Entra app), the
 * helpers degrade to "auth disabled" and the AuthContext serves a
 * deterministic dev-mode identity (see `auth/AuthContext.tsx`).
 */
import { PublicClientApplication } from '@azure/msal-browser';
import type { Configuration } from '@azure/msal-browser';

const clientId = import.meta.env.VITE_AZURE_AD_CLIENT_ID || '';
const tenantId = import.meta.env.VITE_AZURE_AD_TENANT_ID || '';

const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
};

export const loginRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
};

/** True iff a real Entra ID app is configured at build time. */
export function isAuthEnabled(): boolean {
  return !!(clientId && tenantId);
}

let msalPromise: Promise<PublicClientApplication> | null = null;

function initMsal(): Promise<PublicClientApplication> {
  if (!msalPromise) {
    const instance = new PublicClientApplication(msalConfig);
    msalPromise = instance.initialize().then(() => instance);
  }
  return msalPromise;
}

if (isAuthEnabled()) {
  initMsal();
}

export async function getMsalInstance(): Promise<PublicClientApplication> {
  return initMsal();
}
